import { Schema } from "effect";

const BoundedText = Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(240)));
const LocationId = Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(120)));

/**
 * A complete provider-neutral geographic coordinate pair.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const coordinates: AppointmentPrintCoordinates = { latitude: 52.52, longitude: 13.405 };
 * ```
 */
export const AppointmentPrintCoordinates = Schema.Struct({
  latitude: Schema.Number.pipe(Schema.check(Schema.isFinite()), Schema.check(Schema.isBetween({ minimum: -90, maximum: 90 }))),
  longitude: Schema.Number.pipe(Schema.check(Schema.isFinite()), Schema.check(Schema.isBetween({ minimum: -180, maximum: 180 }))),
});

/**
 * Decoded appointment print coordinates.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const coordinates: AppointmentPrintCoordinates = { latitude: 0, longitude: 0 };
 * ```
 */
export type AppointmentPrintCoordinates = typeof AppointmentPrintCoordinates.Type;

/**
 * Publicly printable contact details for a practice location.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const contact: AppointmentPrintPublicContact = { phone: "+49 30 1234" };
 * ```
 */
export const AppointmentPrintPublicContact = Schema.Struct({
  phone: Schema.optional(BoundedText),
  email: Schema.optional(
    Schema.String.pipe(Schema.check(Schema.isMinLength(3)), Schema.check(Schema.isMaxLength(254)), Schema.check(Schema.isPattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))),
  ),
});

/**
 * Decoded public appointment print contact.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const contact: AppointmentPrintPublicContact = { email: "info@example.test" };
 * ```
 */
export type AppointmentPrintPublicContact = typeof AppointmentPrintPublicContact.Type;

/**
 * Vendor-owned identity and address of one appointment location.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const location: AppointmentPrintVendorLocation = { locationId: "loc-1", name: "Praxis", addressLines: ["Markt 1", "10115 Berlin"], addressFingerprint: "abc" };
 * ```
 */
export const AppointmentPrintVendorLocation = Schema.Struct({
  locationId: LocationId,
  name: BoundedText,
  addressLines: Schema.Array(BoundedText).pipe(Schema.check(Schema.isMaxLength(6))),
  addressFingerprint: Schema.String.pipe(Schema.check(Schema.isMinLength(64)), Schema.check(Schema.isMaxLength(64))),
});

/**
 * Decoded vendor location used by appointment printing.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * function locationName(location: AppointmentPrintVendorLocation) { return location.name; }
 * ```
 */
export type AppointmentPrintVendorLocation = typeof AppointmentPrintVendorLocation.Type;

/**
 * Staff-managed fields that supplement one vendor location.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const supplement: AppointmentPrintLocationSupplement = { contact: {}, sourceAddressFingerprint: "a".repeat(64) };
 * ```
 */
export const AppointmentPrintLocationSupplement = Schema.Struct({
  contact: AppointmentPrintPublicContact,
  coordinates: Schema.optional(AppointmentPrintCoordinates),
  sourceAddressFingerprint: Schema.String.pipe(Schema.check(Schema.isMinLength(64)), Schema.check(Schema.isMaxLength(64))),
});

/**
 * Decoded staff-managed location supplement.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * function hasMapPin(value: AppointmentPrintLocationSupplement) { return value.coordinates !== undefined; }
 * ```
 */
export type AppointmentPrintLocationSupplement = typeof AppointmentPrintLocationSupplement.Type;

/**
 * Fixed non-sensitive reasons for absent appointment print metadata.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: AppointmentPrintLocationUnavailableReason = "location-not-found";
 * ```
 */
export const AppointmentPrintLocationUnavailableReason = Schema.Literals(
  ["schedule-not-found",
  "patient-mismatch",
  "calendar-not-found",
  "location-not-configured",
  "location-not-found",
  "ambiguous-location",
  "invalid-vendor-data",
  "timeout",
  "unavailable"],
);

/**
 * Decoded metadata-unavailability reason.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: AppointmentPrintLocationUnavailableReason = "timeout";
 * ```
 */
export type AppointmentPrintLocationUnavailableReason = typeof AppointmentPrintLocationUnavailableReason.Type;

/**
 * Minimal public location result returned to a confirmed FrontDesk session.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const result: AppointmentPrintLocationResult = { outcome: "unavailable", reason: "timeout" };
 * ```
 */
export const AppointmentPrintLocationResult = Schema.Union(
  [Schema.Struct({
    outcome: Schema.Literal("available"),
    location: AppointmentPrintVendorLocation,
    contact: AppointmentPrintPublicContact,
    coordinates: Schema.optional(AppointmentPrintCoordinates),
    coordinatesStatus: Schema.Literals(["available", "missing", "stale"]),
  }),
  Schema.Struct({
    outcome: Schema.Literal("unavailable"),
    reason: AppointmentPrintLocationUnavailableReason,
  })],
);

/**
 * Decoded read-only appointment print location result.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * function isAvailable(result: AppointmentPrintLocationResult) { return result.outcome === "available"; }
 * ```
 */
export type AppointmentPrintLocationResult = typeof AppointmentPrintLocationResult.Type;
